"use client";

import Image from "next/image";
import { useState, useRef } from "react";
import AnimatedContent from "../AnimatedContent";
import { useTranslation } from "react-i18next";
import SectionContainer from "@/common/SectionContainer";

interface Leader {
  name: string;
  title: string;
  position: string;
  image: string;
  description: string[];
  message: string;
}

const StructureSection = () => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const leaders = t("STRUCTURE_SECTION_LEADERS", {
    returnObjects: true,
  }) as Leader[];

  const strengths = t("STRUCTURE_SECTION_STRENGTHS", {
    returnObjects: true,
  }) as string[];

  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollLeft = scrollRef.current.scrollLeft;
      const containerWidth = scrollRef.current.clientWidth;
      const index = Math.round(scrollLeft / containerWidth);
      setActiveIndex(Math.min(index, leaders.length - 1));
    }
  };

  return (
    <SectionContainer
      id="structure-section"
      className="section-shell bg-gray-50 text-gray-900"
    >
      <AnimatedContent className="section-inner section-stack">
        <div className="section-head">
          <h2 className="section-title">{t("STRUCTURE_SECTION_TITLE")}</h2>
          <p className="caption text-gray-600 section-lead whitespace-pre-wrap">
            {t("STRUCTURE_SECTION_SUB_TITLE")}
          </p>
        </div>

        <div>
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex md:grid md:grid-cols-3 gap-6 md:gap-10 lg:gap-12 overflow-x-auto md:overflow-visible snap-x snap-mandatory pb-4 md:pb-0 -mx-6 px-6 md:mx-0 md:px-0 hide-scrollbar items-stretch"
          >
            {leaders.map((person) => (
              <div
                key={person.name}
                className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all p-8 flex flex-col items-center text-center min-w-[82vw] md:min-w-0 snap-center min-h-[650px] md:min-h-[700px]"
              >
                <div className="flex flex-col items-center w-full mb-6">
                  <div className="relative w-[180px] h-[180px] rounded-full overflow-hidden shadow-md mb-6">
                    <Image
                      src={person.image}
                      alt={person.name}
                      fill
                      quality={95}
                      className="object-cover"
                      sizes="(max-width: 768px) 180px, (max-width: 1200px) 180px, 180px"
                    />
                  </div>
                  <h4 className="text-[clamp(1.25rem,2vw,1.5rem)] font-semibold mb-2">
                    {person.name}{" "}
                    <span className="text-gray-500 text-[clamp(1rem,1.8vw,1.25rem)]">
                      {person.title}
                    </span>
                  </h4>
                  <p className="text-[#207AE8] text-[clamp(0.95rem,1.4vw,1.05rem)] font-medium">
                    {person.position}
                  </p>
                </div>
                <ul className=" text-gray-600 text-sm sm:text-base space-y-2 mb-6 text-left w-full flex-1 flex flex-col pl-6">
                  {person.description.map((d) => (
                    <li key={d} className="leading-7! relative">
                      <span className="absolute -left-5 top-1/2 -translate-y-1/2 text-[#207AE8] font-bold">
                        ✓
                      </span>
                      {d}
                    </li>
                  ))}
                </ul>
                <div className="w-full pt-6 border-t border-gray-300 mt-auto">
                  <p className="text-[clamp(0.9rem,1.2vw,1rem)] text-gray-800 font-medium italic whitespace-pre-wrap">
                    {person.message}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex md:hidden justify-center gap-2 -mt-12">
          {leaders.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                activeIndex === i ? "bg-[#207AE8] w-6" : "bg-gray-300"
              }`}
            />
          ))}
        </div>

        <div className="relative hidden md:block w-full md:w-3/4 mx-auto aspect-4/1">
          <Image
            src="https://zipshow-static-web.s3.ap-northeast-2.amazonaws.com/homepage/color-to-life-biz/structure_organization_chart.png"
            alt={t("STRUCTURE_SECTION_ORG_CHART_ALT")}
            fill
            quality={90}
            className="object-contain"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 50vw"
          />
        </div>

        <div className="flex flex-col justify-center gap-8 bg-gradient-to-r from-[#5BBF4D]/10 to-[#207AE8]/10 rounded-2xl p-8 shadow-inner hover:shadow-md transition-all">
          <div className="space-y-2!">
            <h3 className="font-semibold text-[#207AE8]">
              {t("STRUCTURE_SECTION_STRENGTH_TITLE")}
            </h3>
            <p className="text-gray-800">{t("STRUCTURE_SECTION_STRENGTH_SUB_TITLE")}</p>
          </div>
          <div className="space-y-2!">
            {strengths.map((s) => (
              <p
                key={s}
                className="text-gray-800 text-[clamp(0.9rem,1.2vw,1rem)]"
              >
                • {s}
              </p>
            ))}
          </div>
        </div>
      </AnimatedContent>
    </SectionContainer>
  );
};

export default StructureSection;
